import React, { createContext, FC, PropsWithChildren, useMemo, useState } from "react";

import { useSessionStorage } from "react-use";

import { FormDateRange } from "@/components/organisms/FilterDropdown/components/DateRangeForm/DateRangeForm";
import { convertStringDates } from "@/core/date";

export const LOCAL_FILTER_STORAGE_KEY = "localFilter";

export type LocalFilterObjectType =
    | "insuredgood"
    | "contract"
    | "claim"
    | "invoice"
    | "document"
    | "request"
    | "activity";
export type LocalFilterValueType = FormDateRange;

export enum LocalFilterType {
    DATE_RANGE = "date-range",
}

export interface ISavedFilterItem {
    id: string;
    value?: LocalFilterValueType;
}

export interface IFilterItem {
    id: string;
    count: (values: LocalFilterValueType) => number;
    valueDisplay: (values: LocalFilterValueType) => string;
    isNull: (values: LocalFilterValueType) => boolean;
    label: string;
    type: LocalFilterType;
    objectType: LocalFilterObjectType;
}

interface ILocalFilterContext {
    localFilters: ISavedFilterItem[];
    updateFilter?: (id: string, values: LocalFilterValueType) => void;
    isFilterApplied?: (id: string) => boolean;
    getFilterValue?: (id: string) => LocalFilterValueType;
    removeFilter?: (id: string) => void;
    removeAll?: () => void;
}

export const LocalFilterContext = createContext<ILocalFilterContext>({ localFilters: [] });
LocalFilterContext.displayName = "LocalFilterContext";

const updateArray = (fields: ISavedFilterItem[], id: string, newValue: LocalFilterValueType) => {
    const auxFields = [...fields];
    const index = auxFields.findIndex((f: ISavedFilterItem) => f.id === id);

    if (index === -1) {
        auxFields.push({ id, value: newValue });
        return auxFields;
    }
    const updatedValue = { ...auxFields[index], value: newValue };

    return [...auxFields.slice(0, index), updatedValue, ...auxFields.slice(index + 1)];
};

export const LocalFilterProvider: FC<PropsWithChildren> = ({ children }) => {
    const [localFiltersStored, setLocalFiltersStored] = useSessionStorage<ISavedFilterItem[]>(
        LOCAL_FILTER_STORAGE_KEY,
        [],
    );

    const [localFilters, setLocalFilters] = useState<ISavedFilterItem[]>(localFiltersStored || []);

    const contextValue = useMemo(
        (): ILocalFilterContext => ({
            localFilters,
            isFilterApplied(id: string) {
                return localFiltersStored?.find?.((filter) => filter.id === id)?.value !== undefined;
            },
            removeFilter(id: string) {
                setLocalFiltersStored(localFiltersStored.filter((filter) => filter.id !== id));
                setLocalFilters(localFilters.filter((filter) => filter.id !== id));
            },
            getFilterValue(id: string) {
                return convertStringDates(localFiltersStored?.find?.((filter) => filter.id === id)?.value);
            },
            updateFilter(id: string, values: LocalFilterValueType) {
                setLocalFiltersStored(updateArray(localFiltersStored, id, values));

                const value = updateArray(localFilters, id, values);
                setLocalFilters(value);
            },
            removeAll() {
                setLocalFiltersStored([]);
                setLocalFilters([]);
            },
        }),
        [localFilters, localFiltersStored],
    );

    return <LocalFilterContext.Provider value={contextValue}>{children}</LocalFilterContext.Provider>;
};
